/**
 * Rails integration entry point.
 * MUST NOT import React or ReactDOM directly — the host provides them via window.
 */

import './index.css';
import './App.css';
import { mountFrontend } from '@sofatutor/common';
import App from './App';
import {
  defaultShareViewOptions,
  serializeShareViewOptionsToQuery,
  encodeFormulasToString,
  parseShareViewOptionsFromUrl
} from './utils/urlEncoding';
import { createDefaultFormula } from './utils/formulaUtils';

interface FunctionPlaygroundWindow extends Window {
  FunctionPlaygroundFrontend?: any;
  React: any;
  ReactDOM: any;
  __FUNCTION_PLAYGROUND_HOST_REACT?: any;
  __FUNCTION_PLAYGROUND_HOST_REACTDOM?: any;
}

const win = window as FunctionPlaygroundWindow;

type ViewOptions = Partial<typeof defaultShareViewOptions>;

type RailsIntegrationProps = {
  view?: ViewOptions;
  // Legacy alias used by controller: map to view options
  ui?: ViewOptions;
  formulas?: string[];
  grid?: { x: number; y: number } | null;
  // Preferred locale from Rails (e.g., 'de', 'en', 'fr').
  // If provided, this takes precedence and is applied as view.lang
  locale?: string;
  // Tracking params from Rails controller (e.g. consent, ids, env)
  trackingParams?: import('./types/tracking').TrackingParams;
};

// Props accepted by the root App component
type AppProps = {
  basename?: string;
  trackingParams?: import('./types/tracking').TrackingParams;
  isRails?: boolean;
};

function applyPropsToUrl(props: RailsIntegrationProps | undefined) {
  if (!props) return;

  // Apply ShareViewOptions via query params
  if (props.view || props.ui || props.locale) {
    // Merge provided view/UI props over CURRENT URL options (so URL wins for unspecified keys)
    const currentFromUrl = parseShareViewOptionsFromUrl(window.location.search);
    const applied = {
      ...defaultShareViewOptions,
      ...currentFromUrl,
      ...(props.view || {}),
      ...(props.ui || {})
    };
    // Allow top-level locale prop to set language
    if (props.locale && typeof props.locale === 'string') {
      applied.lang = props.locale;
    }
    const query = serializeShareViewOptionsToQuery(applied);
    if (query) {
      const url = new URL(window.location.href);
      // Clear existing related params first for idempotency
      ['layout', 'funcControls', 'fullscreen', 'tools', 'zoom', 'showUnits', 'header', 'admin', 'lang']
        .forEach(k => url.searchParams.delete(k));
      new URLSearchParams(query).forEach((v, k) => url.searchParams.set(k, v));
      window.history.replaceState({}, '', url.toString());
    }
  }

  // Apply formulas if provided
  if (props.formulas && props.formulas.length > 0) {
    const formulas = props.formulas.map(expr => {
      const f = createDefaultFormula('function');
      f.expression = expr;
      return f;
    });
    const encoded = encodeFormulasToString(formulas);
    const url = new URL(window.location.href);
    url.searchParams.set('formulas', encoded);
    window.history.replaceState({}, '', url.toString());
  }
}

function FunctionPlaygroundWrapper(props: RailsIntegrationProps) {
  const React = win.React;

  if (!React) {
    console.error('React is not available in global scope for Function Playground');
    return null;
  }

  // Apply incoming props to URL so providers pick them up consistently
  try {
    applyPropsToUrl(props);
  } catch (e) {
    console.error('Failed to apply Function Playground props:', e);
  }

  // Use Rails mount path as basename so BrowserRouter resolves correctly
  const basename = '/function-playground';
  // Ensure `App` is treated as a component that accepts optional basename and trackingParams
  return React.createElement(
    App as React.ComponentType<AppProps>,
    { basename, trackingParams: (props as any)?.trackingParams, isRails: true }
  );
}

mountFrontend('function-playground', FunctionPlaygroundWrapper);


// Attempts to automatically resize the parent iframe when embedded, if same-origin.
// This enables host pages (e.g., video pages) to resize the iframe to fit content.
// Only works in same-origin scenarios via direct DOM manipulation; cross-origin resizing is not supported.
function setupIframeAutoResize(): void {
  try {
    // Only run when inside an iframe
    if (window.self === window.top) return;

    // Bail out early when we cannot access the parent document (cross-origin)
    const isSameOriginWithParent = (): boolean => {
      try {
        // Accessing parent.document will throw on cross-origin
        void (window.parent as Window | null)?.document;
        return true;
      } catch (_) {
        return false;
      }
    };
    if (!isSameOriginWithParent()) return;

    let lastHeight = 0;

    const safePost = (height: number) => {
      if (!Number.isFinite(height) || height <= 0 || height === lastHeight) return;
      lastHeight = height;
      try {
        // Same-origin optimization: directly resize the hosting iframe if accessible
        try {
          const parentDocument = window.parent?.document as Document | undefined;
          if (parentDocument) {
            const iframes = Array.from(
              parentDocument.querySelectorAll('iframe')
            ) as HTMLIFrameElement[];
            for (const iframe of iframes) {
              if (iframe.contentWindow === window) {
                iframe.style.height = `${height}px`;
                iframe.setAttribute('height', String(height));
                iframe.setAttribute('scrolling', 'no');
                iframe.style.overflow = 'hidden';
                break;
              }
            }
          }
        } catch (_) {
          // Ignore if cross-origin or inaccessible
        }
      } catch (_) {
        // Ignore failures (e.g., tight CSP); parent just won't resize
      }
    };

    let debounceTimer: number | undefined;

    const getContainer = (): HTMLElement => {
      return (
        (document.querySelector('.embedded-view') as HTMLElement) ||
        (document.getElementById('geometry-canvas') as HTMLElement) ||
        (document.getElementById('root') as HTMLElement) ||
        (document.body as HTMLElement)
      );
    };

    const computeAndPost = () => {
      // Prefer layout-derived height based on actual rendered container
      const body = document.body as HTMLElement | null;
      const docEl = document.documentElement as HTMLElement | null;

      let measured = 0;
      try {
        const container = getContainer();

        if (container) {
          const containerRect = container.getBoundingClientRect();
          const computedStyle = window.getComputedStyle(container);
          const marginTop = parseFloat(computedStyle.marginTop || '0');
          const marginBottom = parseFloat(computedStyle.marginBottom || '0');
          // Use scrollHeight/offsetHeight as primary, with rect.height as fallback
          const boxHeight = Math.max(
            container.scrollHeight,
            container.offsetHeight,
            Math.round(containerRect.height)
          );
          measured = Math.round(
            boxHeight + marginTop + marginBottom
          );
        }
      } catch (_) {
        // Fallback to scroll/offset metrics below
      }

      const fallback = Math.max(
        body?.scrollHeight ?? 0,
        docEl?.scrollHeight ?? 0,
        body?.offsetHeight ?? 0,
        docEl?.offsetHeight ?? 0,
        body?.clientHeight ?? 0,
        docEl?.clientHeight ?? 0
      );
      const height = Math.max(measured, fallback);
      // Clamp to a reasonable max to avoid runaway values
      // Tighter upper bound to avoid runaway values in embeds
      const clamped = Math.min(Math.max(height, 100), 3000);
      // Post immediately and once more on the next animation frame
      safePost(clamped);
      window.requestAnimationFrame(() => safePost(clamped));
    };

    const scheduleCompute = (delayMs = 100) => {
      if (debounceTimer !== undefined) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => computeAndPost(), delayMs);
    };

    // Initial measurement
    setTimeout(computeAndPost, 0);

    window.addEventListener('load', () => scheduleCompute(0), { passive: true });
    window.addEventListener('resize', () => scheduleCompute(120), { passive: true });

    // Observe body size changes
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => scheduleCompute(60));
      if (document.body) resizeObserver.observe(document.body);
      if (document.documentElement) resizeObserver.observe(document.documentElement);
      const geometryCanvas = document.getElementById('geometry-canvas');
      if (geometryCanvas) resizeObserver.observe(geometryCanvas);
    }

    // Also watch DOM mutations that might change height without resize
    if (typeof MutationObserver !== 'undefined') {
      const mutationObserver = new MutationObserver(() => scheduleCompute(60));
      const target = getContainer();
      mutationObserver.observe(target, { childList: true, subtree: true });
      // Disconnect on page hide to avoid leaks
      window.addEventListener('pagehide', () => mutationObserver.disconnect(), { once: true });
    }
  } catch (_) {
    // Never crash host due to auto-resize script
  }
}

setupIframeAutoResize();
